import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const laColleSurLoup: ChauffeurPage = {
  slug: "la-colle-sur-loup",
  title: "Chauffeur privé à La Colle-sur-Loup – VTC",
  subtitle:
    "Chauffeur privé et VTC à La Colle-sur-Loup : transfert aéroport de Nice, village des roses et antiquaires, voisin de Saint-Paul-de-Vence.",
  keywords: [
    "chauffeur privé la colle-sur-loup",
    "vtc la colle-sur-loup",
    "chauffeur la colle sur loup",
    "transfert la colle aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à La Colle-sur-Loup",
    subtitle:
      "Le village des roses et des antiquaires, dans la vallée du Loup aux portes de Saint-Paul-de-Vence : transferts aéroport de Nice et mises à disposition.",
    keywords: [
      "VTC La Colle-sur-Loup",
      "chauffeur vallée du Loup",
      "transfert aéroport Nice",
      "chauffeur Saint-Paul-de-Vence",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à La Colle-sur-Loup",
    badges: ["Vallée du Loup", "Voisin de Saint-Paul", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à La Colle-sur-Loup",
    items: [
      {
        title: "Aux portes de Saint-Paul-de-Vence",
        description:
          "La Colle jouxte le village d’art de Saint-Paul : le chauffeur relie les deux en quelques minutes et évite la recherche de stationnement au pied des remparts.",
      },
      {
        title: "Village des roses & antiquaires",
        description:
          "Terre de roseraies et d’antiquaires réputés : une journée flâneuse le long de la vallée, avec un chauffeur dédié qui patiente entre les boutiques.",
      },
      {
        title: "Aéroport de Nice ~35 min",
        description:
          "Rejoignez l’aéroport de Nice Côte d’Azur en environ 35 minutes par Cagnes-sur-Mer et l’A8, avec suivi de vol.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, basés sur la Côte d’Azur, ponctuels et discrets — 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à La Colle-sur-Loup",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge à La Colle-sur-Loup avec suivi de vol, vers et depuis l’aéroport de Nice Côte d’Azur, en environ 35 minutes.",
      },
      {
        title: "Navette Saint-Paul-de-Vence",
        text: "Liaison vers Saint-Paul-de-Vence et la fondation Maeght, dépose au plus près des remparts, reprise sur demande.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée : antiquaires, roseraies, restaurants de la vallée du Loup et villages voisins.",
      },
      {
        title: "Escapade vallée du Loup",
        text: "Itinéraire vers Villeneuve-Loubet, Vence et l’arrière-pays, avec un chauffeur qui connaît les routes du secteur.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis La Colle-sur-Loup : Cannes, Nice, Monaco, Italie et stations de ski, en véhicule confortable.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Vieux village de La Colle",
        description: "Cœur historique, place de la Libération et terrasses.",
      },
      {
        name: "Antiquaires & brocantes",
        description: "Boutiques réputées le long de la route de Saint-Paul.",
      },
      {
        name: "Vallée du Loup",
        description: "Rivière, roseraies et campagne aux portes du village.",
      },
      {
        name: "Saint-Paul-de-Vence",
        description: "Village d’art voisin, fondation Maeght et remparts.",
      },
      {
        name: "Villeneuve-Loubet",
        description: "Vers la mer et le Marina Baie des Anges.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 35 minutes par Cagnes-sur-Mer et l’A8.",
      },
    ],
  },
  generic: {
    heading: "La Colle-sur-Loup, roses et antiquaires du Loup",
    subheading:
      "Un village de la vallée du Loup, voisin de Saint-Paul-de-Vence, entre roseraies et boutiques d’antiquaires.",
    paragraphs: [
      "La Colle-sur-Loup cultive un art de vivre de vallée : terre de roses et d’antiquaires, ses boutiques s’égrènent le long de la route de Saint-Paul et son vieux village invite à la flânerie. Le chauffeur vous dépose au cœur du bourg ou devant les antiquaires et patiente le temps de vos trouvailles.",
      "Voisine immédiate de Saint-Paul-de-Vence, La Colle est le point de départ idéal pour rejoindre le village d’art, la fondation Maeght, Vence ou Villeneuve-Loubet, sans se soucier du stationnement au pied des remparts. Nos chauffeurs relient ces sites en quelques minutes.",
      "Pour l’aéroport de Nice Côte d’Azur, comptez environ 35 minutes par Cagnes-sur-Mer et l’autoroute A8 selon le trafic ; nos chauffeurs bilingues suivent votre vol et ajustent le départ.",
    ],
    bullets: [
      "Liaison Saint-Paul-de-Vence",
      "Antiquaires & roseraies",
      "Suivi de vol & devis immédiat",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à La Colle-sur-Loup",
    steps: [
      "Indiquez votre trajet (village, Saint-Paul, aéroport de Nice) et vos horaires.",
      "Nous confirmons le véhicule adapté et le forfait fixe ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’adresse convenue.",
      "Ajustements en direct selon le trafic et votre programme.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "La Colle-sur-Loup"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Comptez environ 35 minutes par Cagnes-sur-Mer et l’autoroute A8 selon le trafic, avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Assurez-vous la liaison avec Saint-Paul-de-Vence ?",
      a: "Oui, La Colle jouxte Saint-Paul-de-Vence : nous vous déposons au plus près des remparts et de la fondation Maeght, avec reprise sur demande, sans souci de stationnement.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et pendant les événements de Saint-Paul ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Peut-on faire la tournée des antiquaires avec chauffeur ?",
      a: "Oui, en mise à disposition à l’heure ou à la journée, le chauffeur vous conduit d’une boutique à l’autre le long de la vallée du Loup et patiente entre les visites.",
    },
  ],
  relatedLinks: [
    {
      label: "VTC Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Navette aéroport de Nice", href: "/chauffeur/aeroport" },
    { label: "VTC à Nice", href: "/chauffeur/nice" },
    {
      label: "Louer une voiture à Vence",
      href: "/location/location-voiture-vence",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à La Colle-sur-Loup",
    text: "Transfert aéroport de Nice, navette Saint-Paul ou mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé La Colle-sur-Loup — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à La Colle-sur-Loup : transfert aéroport de Nice en ~35 min, roses, antiquaires et navette Saint-Paul-de-Vence. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/la-colle-sur-loup",
  },
};

export default laColleSurLoup;
